<script lang="ts">
	import { Radio, Users, Shield, Trophy, MapPin, PocketKnife } from 'lucide-svelte';
</script>

<div class="container mx-auto px-4 py-8">
	<!-- Hero Section -->
	<div class="mb-12 text-center">
		<h1 class="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl dark:text-white">
			About DSARC
		</h1>
		<p class="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
			Serving the Amateur Radio Community in Moscow, Idaho since 2015
		</p>
	</div>

	<!-- Main Content -->
	<div class="prose prose-lg dark:prose-invert max-w-none">
		<div
			class="rounded-lg border-l-4 border-blue-800 bg-blue-50 p-6 dark:border-blue-400 dark:bg-blue-900/30"
		>
			<p class="mb-4 text-lg leading-relaxed">
				Our club was founded on the ideals of volunteer public service, primarily through the use of
				ham radio for communication in support of public events and emergency communication. Oh, and
				having fun with ham radio!
			</p>
			<p class="text-lg leading-relaxed">
				Some of the ham focused activities we enjoy are DXing, antenna builds, fox hunts, and
				contesting. Our outings focus on a relaxed community atmosphere where all hams are welcome.
				DSARC is based out of Moscow, in Latah County Idaho.
			</p>
		</div>

		<!-- Activities Grid -->
		<div class="my-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
			<div class="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
				<Radio class="mb-4 h-8 w-8 text-blue-800 dark:text-blue-400" />
				<h3 class="mb-2 text-lg font-semibold dark:text-white">DXing</h3>
				<p class="text-gray-600 dark:text-gray-300">
					Long-distance communication with operators around the world.
				</p>
			</div>

			<div class="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
				<PocketKnife class="mb-4 h-8 w-8 text-blue-800 dark:text-blue-400" />
				<h3 class="mb-2 text-lg font-semibold dark:text-white">Antenna Builds</h3>
				<p class="text-gray-600 dark:text-gray-300">
					Hands-on workshops for building and understanding antenna systems.
				</p>
			</div>

			<div class="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
				<MapPin class="mb-4 h-8 w-8 text-blue-800 dark:text-blue-400" />
				<h3 class="mb-2 text-lg font-semibold dark:text-white">Fox Hunts</h3>
				<p class="text-gray-600 dark:text-gray-300">
					Radio direction finding activities and competitions.
				</p>
			</div>

			<div class="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
				<Trophy class="mb-4 h-8 w-8 text-blue-800 dark:text-blue-400" />
				<h3 class="mb-2 text-lg font-semibold dark:text-white">Contesting</h3>
				<p class="text-gray-600 dark:text-gray-300">
					Participating in amateur radio contests and competitions.
				</p>
			</div>

			<div class="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
				<Shield class="mb-4 h-8 w-8 text-blue-800 dark:text-blue-400" />
				<h3 class="mb-2 text-lg font-semibold dark:text-white">Emergency Comms</h3>
				<p class="text-gray-600 dark:text-gray-300">
					Supporting emergency communications and public service events.
				</p>
			</div>

			<div class="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
				<Users class="mb-4 h-8 w-8 text-blue-800 dark:text-blue-400" />
				<h3 class="mb-2 text-lg font-semibold dark:text-white">Community</h3>
				<p class="text-gray-600 dark:text-gray-300">
					Building relationships within the amateur radio community.
				</p>
			</div>
		</div>

		<!-- Partnerships Section -->
		<div class="my-12 rounded-lg bg-gray-50 p-8 dark:bg-gray-800">
			<h2 class="mb-6 text-2xl font-bold dark:text-white">Our Partnerships</h2>
			<div class="space-y-6">
				<div>
					<h3 class="mb-2 text-xl font-semibold dark:text-white">Latah County Aux Comm Team</h3>
					<p class="text-gray-600 dark:text-gray-300">
						DSARC is proud to support the Latah County Aux Comm Team in its training efforts,
						helping to ensure our community is prepared for emergency communications needs.
					</p>
				</div>
				<div>
					<h3 class="mb-2 text-xl font-semibold dark:text-white">Northwest Radio Supply</h3>
					<p class="text-gray-600 dark:text-gray-300">
						We are proudly supported by Northwest Radio Supply, providing our members with access to
						quality amateur radio equipment and supplies.
					</p>
				</div>
			</div>
		</div>

		<!-- Location Section -->
		<div class="my-12 rounded-lg bg-white p-8 shadow-sm dark:bg-gray-800">
			<h2 class="mb-6 text-2xl font-bold dark:text-white">Our Location</h2>
			<div class="flex items-start space-x-4">
				<MapPin class="h-6 w-6 flex-shrink-0 text-blue-800 dark:text-blue-400" />
				<div>
					<h3 class="mb-2 text-xl font-semibold dark:text-white">Moscow, Idaho</h3>
					<p class="text-gray-600 dark:text-gray-300">
						Based in the heart of Latah County, we serve the amateur radio community throughout the
						Palouse region.
					</p>
					<button
						class="mt-4 inline-flex items-center rounded-lg bg-blue-800 px-4 py-2 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
					>
						Get Directions
					</button>
				</div>
			</div>
		</div>

		<!-- Join Us Section -->
		<div class="my-12 rounded-lg bg-blue-800 p-8 text-white dark:bg-blue-900">
			<h2 class="mb-4 text-2xl font-bold">Join Our Community</h2>
			<p class="mb-6">
				Whether you're a seasoned ham operator or just getting started, DSARC welcomes you to join
				our community of radio enthusiasts.
			</p>
			<div class="flex space-x-4">
				<a href="/membership" class="rounded-lg bg-white px-4 py-2 text-blue-800 hover:bg-gray-100">
					Become a Member
				</a>
				<a
					href="/calendar"
					class="rounded-lg border border-white px-4 py-2 hover:bg-blue-700 dark:hover:bg-blue-800"
				>
					View Events
				</a>
			</div>
		</div>
	</div>
</div>
